import { useState, useEffect, useRef } from "react";
import { Play, Plus, Image, Youtube, Trash2, MapPin, Calendar, BookOpen, Heart, School, Moon, Package, Upload, Camera, Film, Link, X, Star, Globe, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL - handles all URL formats
const getImageUrl = (url) => {
  if (!url) return '';
  
  // If it's a relative URL, prepend the backend URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  // If it's an old preview URL, replace with current backend URL
  if (url.includes('.preview.emergentagent.com') || url.includes('.emergent.host')) {
    const path = url.replace(/https?:\/\/[^\/]+/, '');
    return `${BACKEND_URL}${path}`;
  }
  
  // Otherwise return as is (external URLs)
  return url;
};

const eventHighlights = [
  { icon: Moon, title: "Overnight Sleepover", description: "We sleep outside overnight to raise awareness" },
  { icon: MapPin, title: "Pete's Fruit Market", description: "MLK Dr & North Ave, Milwaukee" },
  { icon: Calendar, title: "Bronzeville Week", description: "Annual event during Bronzeville Week celebration" },
  { icon: School, title: "Schools in Need", description: "Supporting local schools with supplies" }
];

const suppliesList = [
  "Backpacks",
  "Notebooks & Paper",
  "Pencils & Pens",
  "Crayons & Markers",
  "Folders & Binders",
  "Rulers & Scissors",
  "Glue Sticks",
  "Calculators"
];

export default function SleepoverEducation() {
  const { isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [addPhotoOpen, setAddPhotoOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState("upload"); // "url" or "upload" - default to upload for phone users
  const [videoUploadMode, setVideoUploadMode] = useState("youtube"); // "youtube" or "upload"
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newVideo, setNewVideo] = useState({ title: "", description: "", youtube_url: "", video_url: "" });
  const [newPhoto, setNewPhoto] = useState({ title: "", description: "", image_url: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const [videosRes, photosRes] = await Promise.all([
        axios.get(`${API}/sleepover/videos`),
        axios.get(`${API}/sleepover/photos`)
      ]);
      setVideos(videosRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const validFiles = [];
    const previews = [];
    
    for (const file of files) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      // Check file type
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file.`);
        continue;
      }
      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setPreviewUrls(previews);
      setSelectedFile(validFiles[0]); // Keep for backward compatibility
      setPreviewUrl(previews[0]);
      toast.success(`${validFiles.length} photo(s) selected! Tap 'Upload Photos' to add them.`);
    }
  };

  const handleVideoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error("Video is too large. Maximum size is 100MB.");
        return;
      }
      console.log("Video selected:", file.name, file.size, file.type);
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      toast.success("Video selected! Add a title and tap 'Add Video'");
    }
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    
    if (uploadMode === "upload" && selectedFiles.length > 0) {
      setUploading(true);
      setUploadProgress(0);
      
      const uploadedPhotos = [];
      const totalFiles = selectedFiles.length;
      
      try {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const formData = new FormData();
          formData.append("file", file);
          formData.append("title", newPhoto.title || file.name.split('.')[0]);
          formData.append("description", newPhoto.description || "");
          
          const uploadRes = await axios.post(`${API}/upload/image`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000
          });
          
          const photoData = {
            title: newPhoto.title || file.name.split('.')[0],
            description: newPhoto.description,
            image_url: uploadRes.data.url
          };
          
          const response = await axios.post(`${API}/sleepover/photos`, photoData);
          uploadedPhotos.push(response.data);
          
          setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
        }
        
        setPhotos([...uploadedPhotos.reverse(), ...photos]);
        resetPhotoForm();
        toast.success(`Successfully uploaded ${uploadedPhotos.length} photo(s)!`);
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(error.response?.data?.detail || "Failed to upload photos");
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    } else if (uploadMode === "upload" && selectedFile) {
      if (!newPhoto.title) {
        toast.error("Please enter a title");
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("title", newPhoto.title);
        formData.append("description", newPhoto.description || "");
        
        console.log("Uploading file:", selectedFile.name, selectedFile.size, selectedFile.type);
        
        const uploadRes = await axios.post(`${API}/upload/image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000, // 60 second timeout for large files
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log("Upload progress:", percentCompleted + "%");
          }
        });
        
        console.log("Upload response:", uploadRes.data);
        
        // Create photo record with uploaded URL
        const photoData = {
          title: newPhoto.title,
          description: newPhoto.description,
          image_url: uploadRes.data.url
        };
        
        console.log("Creating photo record:", photoData);
        
        const response = await axios.post(`${API}/sleepover/photos`, photoData);
        setPhotos([response.data, ...photos]);
        resetPhotoForm();
        toast.success("Photo uploaded successfully!");
      } catch (error) {
        console.error("Upload error:", error);
        console.error("Error response:", error.response?.data);
        const errorMsg = error.response?.data?.detail || error.message || "Failed to upload photo";
        toast.error(errorMsg);
      } finally {
        setUploading(false);
      }
    } else {
      // URL mode
      if (!newPhoto.title || !newPhoto.image_url) {
        toast.error("Please fill in required fields");
        return;
      }
      setUploading(true);
      try {
        const response = await axios.post(`${API}/sleepover/photos`, newPhoto);
        setPhotos([response.data, ...photos]);
        resetPhotoForm();
        toast.success("Photo added successfully!");
      } catch (error) {
        toast.error("Failed to add photo");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleUploadVideo = async (e) => {
    e.preventDefault();
    
    if (videoUploadMode === "upload" && selectedFile) {
      if (!newVideo.title) {
        toast.error("Please enter a title");
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("title", newVideo.title);
        formData.append("description", newVideo.description || "");
        
        const uploadRes = await axios.post(`${API}/upload/video`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        // Create video record with uploaded URL
        const videoData = {
          title: newVideo.title,
          description: newVideo.description,
          video_url: uploadRes.data.url,
          is_upload: true
        };
        
        const response = await axios.post(`${API}/sleepover/videos`, videoData);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video uploaded successfully!");
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload video. File may be too large.");
      } finally {
        setUploading(false);
      }
    } else if (videoUploadMode === "url") {
      // Video URL mode (external video links)
      if (!newVideo.title || !newVideo.video_url) {
        toast.error("Please fill in title and video URL");
        return;
      }
      setUploading(true);
      try {
        const videoData = {
          title: newVideo.title,
          description: newVideo.description,
          video_url: newVideo.video_url,
          is_upload: true
        };
        const response = await axios.post(`${API}/sleepover/videos`, videoData);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video added successfully!");
      } catch (error) {
        toast.error("Failed to add video");
      } finally {
        setUploading(false);
      }
    } else {
      // YouTube mode
      if (!newVideo.title || !newVideo.youtube_url) {
        toast.error("Please fill in required fields");
        return;
      }
      setUploading(true);
      try {
        const response = await axios.post(`${API}/sleepover/videos`, {
          ...newVideo,
          is_upload: false
        });
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video added successfully!");
      } catch (error) {
        toast.error("Failed to add video");
      } finally {
        setUploading(false);
      }
    }
  };

  const resetPhotoForm = () => {
    setNewPhoto({ title: "", description: "", image_url: "" });
    setSelectedFile(null);
    setSelectedFiles([]);
    setPreviewUrl(null);
    setPreviewUrls([]);
    setAddPhotoOpen(false);
    setUploadMode("upload");
    setUploadProgress(0);
  };

  const resetVideoForm = () => {
    setNewVideo({ title: "", description: "", youtube_url: "", video_url: "" });
    setSelectedFile(null);
    setPreviewUrl(null);
    setAddVideoOpen(false);
    setVideoUploadMode("youtube");
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previewUrls];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
    if (newFiles.length > 0) {
      setSelectedFile(newFiles[0]);
      setPreviewUrl(newPreviews[0]);
    } else {
      setSelectedFile(null);
      setPreviewUrl(null);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await axios.delete(`${API}/sleepover/videos/${videoId}`);
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success("Video deleted");
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/sleepover/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleToggleVideoFeatured = async (videoId) => {
    try {
      const response = await axios.patch(`${API}/sleepover/videos/${videoId}/feature`);
      setVideos(videos.map(v => 
        v.id === videoId ? { ...v, is_featured: response.data.is_featured } : v
      ));
      toast.success(response.data.is_featured ? "Video featured!" : "Video unfeatured");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  const handleTogglePhotoFeatured = async (photoId) => {
    try {
      const response = await axios.patch(`${API}/sleepover/photos/${photoId}/feature`);
      setPhotos(photos.map(p => 
        p.id === photoId ? { ...p, is_featured: response.data.is_featured } : p
      ));
      toast.success(response.data.is_featured ? "Photo featured!" : "Photo unfeatured");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  // Sort items with featured first
  const sortedVideos = [...videos].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
  const sortedPhotos = [...photos].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
  const featuredCount = videos.filter(v => v.is_featured).length + photos.filter(p => p.is_featured).length;

  return (
    <div data-testid="sleepover-education-page" className="bg-gradient-to-b from-[#1e3a5f] to-[#0d1b2a] min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        {/* Background stars effect */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-2 h-2 bg-yellow-300 rounded-full animate-pulse" />
          <div className="absolute top-20 right-20 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
          <div className="absolute top-40 left-1/4 w-1.5 h-1.5 bg-yellow-200 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-32 right-1/3 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
          <div className="absolute bottom-20 left-1/3 w-2 h-2 bg-yellow-300 rounded-full animate-pulse" style={{ animationDelay: '0.7s' }} />
          <div className="absolute bottom-40 right-1/4 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '1.2s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/30 px-4 py-2 rounded-full mb-6">
              <Moon className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-300 text-sm font-semibold">Bronzeville Week Event</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Outfit'] text-white mb-6 leading-tight">
              Sleepover for <span className="text-yellow-400">Education</span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              We sleep outside overnight in front of <span className="text-yellow-400 font-semibold">Pete's Fruit Market</span> on MLK Dr & North Ave during Bronzeville Week to collect school supplies for schools in need.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <MapPin className="w-5 h-5 text-yellow-400" />
                <span className="text-white">Pete's Fruit Market</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Calendar className="w-5 h-5 text-yellow-400" />
                <span className="text-white">Bronzeville Week</span>
              </div>
            </div>

            {/* School Supplies Icon */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="w-32 h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-yellow-500/30">
                  <BookOpen className="w-16 h-16 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Highlights */}
      <section className="py-12 bg-[#0d1b2a]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {eventHighlights.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-yellow-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-7 h-7 text-yellow-400" />
                </div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* School Supplies Section */}
      <section className="py-16 bg-[#1e3a5f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-6">
                Help Us Collect School Supplies
              </h2>
              <p className="text-slate-300 mb-8 leading-relaxed">
                Every child deserves the tools they need to succeed in school. During our overnight sleepover, we collect essential school supplies for students at schools in need throughout Milwaukee.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {suppliesList.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white/5 rounded-lg px-4 py-3">
                    <Package className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                    <span className="text-white text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-3xl p-8 border border-yellow-500/20">
              <h3 className="text-xl font-bold text-white mb-4">Want to Donate Supplies?</h3>
              <p className="text-slate-300 mb-6">
                You can drop off school supplies during Bronzeville Week at Pete's Fruit Market or contact us to arrange a pickup.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-slate-300">
                  <MapPin className="w-5 h-5 text-yellow-400" />
                  <span>MLK Dr & North Ave, Milwaukee</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <Calendar className="w-5 h-5 text-yellow-400" />
                  <span>During Bronzeville Week</span>
                </div>
              </div>
              <a href="/contact">
                <Button className="mt-6 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-full px-6">
                  Contact Us to Donate
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Media Gallery */}
      <section className="py-16 bg-[#0d1b2a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white">
                Photos & Videos
              </h2>
              <p className="text-slate-400 mt-2">Memories from our Sleepover for Education events</p>
            </div>
            <div className="flex gap-3">
              {/* Add Video Dialog */}
              <Dialog open={addVideoOpen} onOpenChange={(open) => { if (!open) resetVideoForm(); else setAddVideoOpen(true); }}>
                <DialogTrigger asChild>
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-full" data-testid="add-sleepover-video-btn">
                    <Film className="w-4 h-4 mr-2" />
                    Add Video
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1e3a5f] border-slate-700 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] text-white">Add Video</DialogTitle>
                  </DialogHeader>
                  
                  {/* Video Mode Toggle */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      type="button"
                      variant={videoUploadMode === "youtube" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "youtube" ? "bg-yellow-500 text-black" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("youtube"); setSelectedFile(null); setPreviewUrl(null); }}
                    >
                      <Youtube className="w-4 h-4 mr-2" />
                      YouTube
                    </Button>
                    <Button
                      type="button"
                      variant={videoUploadMode === "url" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "url" ? "bg-yellow-500 text-black" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("url"); setSelectedFile(null); setPreviewUrl(null); setNewVideo({ ...newVideo, youtube_url: "" }); }}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Video URL
                    </Button>
                    <Button
                      type="button"
                      variant={videoUploadMode === "upload" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "upload" ? "bg-yellow-500 text-black" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("upload"); setNewVideo({ ...newVideo, youtube_url: "", video_url: "" }); }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>

                  <form onSubmit={handleUploadVideo} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-300">Title *</Label>
                      <Input
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="Video title"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        required
                      />
                    </div>

                    {videoUploadMode === "youtube" && (
                      <div>
                        <Label className="text-slate-300">YouTube URL *</Label>
                        <Input
                          value={newVideo.youtube_url}
                          onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                        <p className="text-xs text-slate-400 mt-1">Paste a YouTube video link</p>
                      </div>
                    )}

                    {videoUploadMode === "url" && (
                      <div>
                        <Label className="text-slate-300">Video URL *</Label>
                        <Input
                          value={newVideo.video_url}
                          onChange={(e) => setNewVideo({ ...newVideo, video_url: e.target.value })}
                          placeholder="https://example.com/video.mp4"
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                        <p className="text-xs text-slate-400 mt-1">Direct link to video file (MP4, WebM, etc.) or embed URL</p>
                      </div>
                    )}

                    {videoUploadMode === "upload" && (
                      <div>
                        <Label className="text-slate-300">Select Video from Phone *</Label>
                        <input
                          ref={videoFileInputRef}
                          type="file"
                          accept="video/*"
                          capture="environment"
                          onChange={handleVideoFileSelect}
                          className="hidden"
                          id="video-upload-input"
                        />
                        {/* Primary upload button for mobile */}
                        <label 
                          htmlFor="video-upload-input"
                          className="mt-2 flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-yellow-700 touch-manipulation"
                        >
                          <Film className="w-5 h-5" />
                          {selectedFile ? "Change Video" : "Choose Video from Phone"}
                        </label>
                        
                        {/* Preview area */}
                        {previewUrl && (
                          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
                            <video src={previewUrl} className="max-h-32 mx-auto rounded-lg" controls />
                            <p className="text-sm text-slate-400 text-center mt-2">{selectedFile?.name}</p>
                            <p className="text-xs text-green-400 text-center mt-1">✓ Video selected - Ready to upload</p>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              className="w-full mt-2 text-red-400 hover:text-red-300"
                              onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                            >
                              <X className="w-4 h-4 mr-1" /> Remove Video
                            </Button>
                          </div>
                        )}
                        
                        {!previewUrl && (
                          <p className="text-xs text-slate-400 text-center mt-2">
                            Supports MP4, MOV, WebM • Max 100MB
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-slate-300">Description</Label>
                      <Textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                        placeholder="Brief description"
                        className="mt-1 bg-slate-800 border-slate-600 text-white resize-none"
                        rows={2}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold" 
                      disabled={uploading || (videoUploadMode === "upload" && !selectedFile) || (videoUploadMode === "youtube" && !newVideo.youtube_url) || (videoUploadMode === "url" && !newVideo.video_url)}
                    >
                      {uploading ? "Uploading..." : "Add Video"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Add Photo Dialog */}
              <Dialog open={addPhotoOpen} onOpenChange={(open) => { if (!open) resetPhotoForm(); else setAddPhotoOpen(true); }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-500 hover:bg-blue-600 text-white rounded-full" data-testid="add-sleepover-photo-btn">
                    <Camera className="w-4 h-4 mr-2" />
                    Add Photo
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1e3a5f] border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] text-white">Add Photo</DialogTitle>
                  </DialogHeader>
                  
                  {/* Upload Mode Toggle */}
                  <div className="flex gap-2 mt-4">
                    <Button
                      type="button"
                      variant={uploadMode === "upload" ? "default" : "outline"}
                      className={`flex-1 ${uploadMode === "upload" ? "bg-blue-500" : "border-slate-600 text-white"}`}
                      onClick={() => { setUploadMode("upload"); setNewPhoto({ ...newPhoto, image_url: "" }); }}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      From Phone
                    </Button>
                    <Button
                      type="button"
                      variant={uploadMode === "url" ? "default" : "outline"}
                      className={`flex-1 ${uploadMode === "url" ? "bg-blue-500" : "border-slate-600 text-white"}`}
                      onClick={() => { setUploadMode("url"); setSelectedFile(null); setSelectedFiles([]); setPreviewUrl(null); setPreviewUrls([]); }}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Image URL
                    </Button>
                  </div>

                  <form onSubmit={handleUploadPhoto} className="space-y-4 mt-4">
                    {uploadMode === "upload" ? (
                      <div>
                        <Label className="text-slate-300">Select Photos *</Label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                          id="photo-upload-input"
                        />
                        {/* Primary upload button for mobile */}
                        <label 
                          htmlFor="photo-upload-input"
                          className="mt-2 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-blue-800 touch-manipulation"
                        >
                          <Camera className="w-5 h-5" />
                          {selectedFiles.length > 0 ? `${selectedFiles.length} Photo(s) Selected - Tap to Add More` : "Choose Photos from Phone"}
                        </label>
                        
                        {/* Multiple Preview area - limited to 6 visible */}
                        {previewUrls.length > 0 && (
                          <div className="mt-4 p-3 bg-slate-800 rounded-xl">
                            <div className="grid grid-cols-4 gap-1 mb-2 max-h-24 overflow-hidden">
                              {previewUrls.slice(0, 8).map((url, index) => (
                                <div key={index} className="relative">
                                  <img src={url} alt={`Preview ${index + 1}`} className="w-full h-12 object-cover rounded" />
                                  <button
                                    type="button"
                                    onClick={() => removeSelectedFile(index)}
                                    className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"
                                  >
                                    <X className="w-2 h-2 text-white" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            {previewUrls.length > 8 && (
                              <p className="text-xs text-slate-400 text-center">+{previewUrls.length - 8} more photos</p>
                            )}
                            <p className="text-xs text-green-400 text-center">✓ {selectedFiles.length} photo(s) ready</p>
                            
                            {/* Upload Progress */}
                            {uploading && uploadProgress > 0 && (
                              <div className="mt-2">
                                <div className="w-full bg-slate-700 rounded-full h-2">
                                  <div 
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                                    style={{ width: `${uploadProgress}%` }}
                                  />
                                </div>
                                <p className="text-xs text-center text-slate-400 mt-1">Uploading... {uploadProgress}%</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {previewUrls.length === 0 && (
                          <p className="text-xs text-slate-400 text-center mt-2">
                            Select multiple photos at once • Max 10MB each
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <Label className="text-slate-300">Image URL *</Label>
                        <Input
                          value={newPhoto.image_url}
                          onChange={(e) => setNewPhoto({ ...newPhoto, image_url: e.target.value })}
                          placeholder="https://..."
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                    )}

                    {/* Upload Button - Always visible */}
                    <Button 
                      type="submit" 
                      className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg font-bold sticky bottom-0" 
                      disabled={uploading || (uploadMode === "upload" && selectedFiles.length === 0) || (uploadMode === "url" && !newPhoto.image_url)}
                    >
                      {uploading ? `Uploading... ${uploadProgress}%` : selectedFiles.length > 1 ? `📤 Upload ${selectedFiles.length} Photos` : "📤 Upload Photo"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <Tabs defaultValue="photos" className="w-full">
            <TabsList className="bg-slate-800 border border-slate-700 mb-8">
              <TabsTrigger value="photos" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                <Image className="w-4 h-4 mr-2" />
                Photos ({photos.length})
              </TabsTrigger>
              <TabsTrigger value="videos" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
                <Film className="w-4 h-4 mr-2" />
                Videos ({videos.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="photos">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner w-10 h-10 border-blue-500" />
                </div>
              ) : photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {sortedPhotos.map((photo) => (
                    <div 
                      key={photo.id} 
                      className={`relative aspect-square bg-slate-800 rounded-xl overflow-hidden group cursor-pointer ${photo.is_featured ? 'ring-2 ring-amber-400' : ''}`}
                      onClick={() => setSelectedPhoto(photo)}
                    >
                      {/* Featured Badge */}
                      {photo.is_featured && (
                        <div className="absolute top-2 left-2 z-10 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Star className="w-3 h-3" fill="currentColor" />
                          Featured
                        </div>
                      )}
                      <img 
                        src={getImageUrl(photo.image_url)} 
                        alt={photo.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <h3 className="text-white font-medium text-sm line-clamp-2">{photo.title}</h3>
                        </div>
                      </div>
                      {/* Action buttons */}
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); handleTogglePhotoFeatured(photo.id); }} className={`p-1.5 rounded-full transition-colors ${photo.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-black/50 text-white hover:bg-amber-400 hover:text-amber-900'}`}>
                          <Star className="w-4 h-4" fill={photo.is_featured ? "currentColor" : "none"} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }} className="p-1.5 bg-black/50 rounded-full text-white hover:bg-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-slate-800/30 rounded-2xl border border-slate-700">
                  <Camera className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Photos Yet</h3>
                  <p className="text-slate-400 mb-6">Add photos from your phone or paste image URLs.</p>
                  <Button onClick={() => setAddPhotoOpen(true)} className="bg-blue-500 hover:bg-blue-600">
                    <Plus className="w-5 h-5 mr-2" />
                    Add First Photo
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="videos">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner w-10 h-10 border-yellow-500" />
                </div>
              ) : videos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedVideos.map((video) => (
                    <div 
                      key={video.id} 
                      className={`bg-slate-800/50 rounded-xl overflow-hidden group hover:ring-2 hover:ring-yellow-500 transition-all ${video.is_featured ? 'ring-2 ring-amber-400' : ''}`}
                    >
                      <div 
                        className="relative aspect-video bg-slate-900 cursor-pointer"
                        onClick={() => setSelectedVideo(video)}
                      >
                        {/* Featured Badge */}
                        {video.is_featured && (
                          <div className="absolute top-2 left-2 z-10 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Star className="w-3 h-3" fill="currentColor" />
                            Featured
                          </div>
                        )}
                        {video.is_upload ? (
                          video.video_url?.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                            <video src={getImageUrl(video.video_url)} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-slate-800 flex flex-col items-center justify-center">
                              <Globe className="w-12 h-12 text-slate-500 mb-2" />
                              <span className="text-xs text-slate-500">External Video</span>
                            </div>
                          )
                        ) : (
                          <img 
                            src={video.thumbnail_url} 
                            alt={video.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/640x360/1e3a5f/eab308?text=Video";
                            }}
                          />
                        )}
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center">
                            <Play className="w-8 h-8 text-black ml-1" />
                          </div>
                        </div>
                        {/* Action buttons */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); handleToggleVideoFeatured(video.id); }} className={`p-1.5 rounded-full transition-colors ${video.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-black/50 text-white hover:bg-amber-400 hover:text-amber-900'}`}>
                            <Star className="w-4 h-4" fill={video.is_featured ? "currentColor" : "none"} />
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex justify-between items-start gap-2">
                          <h3 className="font-semibold text-white line-clamp-2">{video.title}</h3>
                          <button
                            onClick={() => handleDeleteVideo(video.id)}
                            className="p-1 text-slate-500 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {video.description && (
                          <p className="text-sm text-slate-400 mt-2 line-clamp-2">{video.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-slate-800/30 rounded-2xl border border-slate-700">
                  <Film className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Videos Yet</h3>
                  <p className="text-slate-400 mb-6">Add videos from YouTube or upload from your phone.</p>
                  <Button onClick={() => setAddVideoOpen(true)} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                    <Plus className="w-5 h-5 mr-2" />
                    Add First Video
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-yellow-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-4">
            Join Us for the Next Sleepover
          </h2>
          <p className="text-yellow-100 mb-8 text-lg">
            Help us make a difference in the lives of Milwaukee students. Volunteer, donate supplies, or spread the word!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/volunteer">
              <Button className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-3 font-semibold">
                Volunteer
              </Button>
            </a>
            <a href="/contact">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold">
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 bg-[#0d1b2a] border-slate-700 overflow-hidden">
          <DialogTitle className="sr-only">{selectedVideo?.title}</DialogTitle>
          {selectedVideo && (
            <div>
              {selectedVideo.is_upload ? (
                selectedVideo.video_url?.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                  <video src={getImageUrl(selectedVideo.video_url)} controls autoPlay className="w-full max-h-[70vh]" />
                ) : (
                  <div className="aspect-video bg-slate-900 flex flex-col items-center justify-center p-8">
                    <Globe className="w-16 h-16 text-slate-500 mb-4" />
                    <p className="text-slate-400 text-center mb-6">This video is hosted on an external website</p>
                    <a href={selectedVideo.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-full font-semibold transition-colors">
                      <ExternalLink className="w-5 h-5" />
                      Watch on External Site
                    </a>
                  </div>
                )
              ) : (
                <div className="youtube-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1`}
                    title={selectedVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedVideo.title}</h3>
                {selectedVideo.description && (
                  <p className="text-slate-400">{selectedVideo.description}</p>
                )}
                {selectedVideo.is_upload && selectedVideo.video_url && !selectedVideo.video_url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) && (
                  <a href={selectedVideo.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-yellow-400 hover:text-yellow-300 mt-3 text-sm">
                    {selectedVideo.video_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 bg-[#0d1b2a] border-slate-700 overflow-hidden">
          <DialogTitle className="sr-only">{selectedPhoto?.title}</DialogTitle>
          {selectedPhoto && (
            <div>
              <img 
                src={getImageUrl(selectedPhoto.image_url)} 
                alt={selectedPhoto.title}
                className="w-full max-h-[70vh] object-contain bg-black"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedPhoto.title}</h3>
                {selectedPhoto.description && (
                  <p className="text-slate-400">{selectedPhoto.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
